import React from 'react';
import { createPortal } from 'react-dom';

interface OptimizationProgressProps {
  message: string;
  isVisible: boolean;
  type?: 'loading' | 'success';
}

export const OptimizationProgress: React.FC<OptimizationProgressProps> = ({ 
  message, 
  isVisible,
  type = 'loading'
}) => {
  if (!isVisible) return null;

  const bgColor = type === 'success' ? 'bg-green-500' : 'bg-blue-500';

  return createPortal(
    <div className="fixed bottom-4 right-4 z-50">
      <div className={`${bgColor} text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2 min-w-[300px]`}>
        {type === 'loading' && (
          <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        )}
        <span className="font-medium">{message}</span>
      </div>
    </div>,
    document.body
  );
};

export const OptimizationContext = React.createContext<{
  showProgress: (message: string, type?: 'loading' | 'success') => void;
  hideProgress: () => void;
}>({
  showProgress: () => {},
  hideProgress: () => {},
});

export const OptimizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [progressState, setProgressState] = React.useState<{
    message: string;
    isVisible: boolean;
    type: 'loading' | 'success';
  }>({
    message: '',
    isVisible: false,
    type: 'loading'
  });

  const showProgress = React.useCallback((message: string, type: 'loading' | 'success' = 'loading') => {
    setProgressState({ message, isVisible: true, type });
  }, []);

  const hideProgress = React.useCallback(() => {
    setProgressState(prev => ({ ...prev, isVisible: false }));
  }, []);

  return (
    <OptimizationContext.Provider value={{ showProgress, hideProgress }}>
      {children}
      <OptimizationProgress {...progressState} />
    </OptimizationContext.Provider>
  );
};

export const useOptimization = () => {
  return React.useContext(OptimizationContext);
};